// src/features/AIRecommendations/hooks/useAIRecommendations.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNotifications } from '../../../context/NotificationContext';
import { useI18n } from '../../../i18n/i18n';

// Детектор аномалій
const detectAnomalies = (data) => {
  const anomalies = [];
  
  if (data.efficiency && data.efficiency < 50) {
    anomalies.push({
      type: 'efficiency',
      severity: 'critical',
      typeKey: 'anomaly_type_efficiency',
      value: data.efficiency,
      expected: 70,
      description: 'Critical low efficiency detected'
    });
  }
  
  if (data.waterConsumption && data.waterConsumption > data.waterThreshold * 1.3) {
    anomalies.push({
      type: 'water_consumption',
      severity: 'high',
      typeKey: 'anomaly_type_water',
      value: data.waterConsumption,
      expected: data.waterThreshold,
      description: 'High water consumption detected'
    });
  }
  
  if (data.propaneConsumption && data.propaneConsumption > data.propaneThreshold * 1.2) {
    anomalies.push({
      type: 'propane_consumption', 
      severity: 'medium',
      typeKey: 'anomaly_type_propane',
      value: data.propaneConsumption,
      expected: data.propaneThreshold,
      description: 'High propane consumption detected'
    });
  }
  
  return anomalies;
};

// Аналіз даних для рекомендацій
const analyzeDataForRecommendations = (productionData, meterData, maintenanceData) => {
  const recommendations = [];
  
  const avgEfficiency = productionData.reduce((sum, item) => sum + (item.efficiency || 0), 0) / productionData.length;
  if (avgEfficiency < 70) {
    recommendations.push({
      id: `eff-${Date.now()}`,
      type: 'efficiency',
      priority: avgEfficiency < 50 ? 'critical' : 'high',
      titleKey: 'recommendation_efficiency_consistency',
      messageKey: 'efficiency_consistency_message',
      messageParams: { 
        consistency: Math.round(avgEfficiency),
        target: 80
      },
      actions: ['standardize_processes', 'implement_checklists'],
      impact: 'high_production',
      urgency: avgEfficiency < 50 ? 'immediate' : 'high',
      implementationTime: '2-4 weeks'
    });
  }
  
  const recentWaterUsage = meterData
    .filter(m => m.type === 'water')
    .slice(-7)
    .reduce((sum, m) => sum + (m.consumption || 0), 0);
  
  if (recentWaterUsage > 5000) {
    recommendations.push({
      id: `water-${Date.now()}`,
      type: 'resource',
      priority: 'medium',
      titleKey: 'recommendation_water_optimization',
      messageKey: 'water_optimization_potential',
      messageParams: { 
        potential: 15,
        current: Math.round(recentWaterUsage / 7)
      },
      actions: ['optimize_water_usage', 'check_equipment_water'],
      impact: 'resource_savings',
      urgency: 'medium',
      implementationTime: '1-2 weeks'
    });
  }
  
  const urgentMaintenance = maintenanceData.filter(m => m.daysUntilDue <= 2);
  if (urgentMaintenance.length > 0) {
    urgentMaintenance.forEach(maintenance => {
      recommendations.push({
        id: `maint-${maintenance.id}`,
        type: 'maintenance',
        priority: 'critical',
        titleKey: 'alert_maintenance_reminder',
        messageKey: 'maintenance_message',
        messageParams: {
          equipment: maintenance.equipment,
          days: maintenance.daysUntilDue,
          type: maintenance.type
        },
        actions: ['schedule', 'inspect'],
        impact: 'efficiency_gain',
        urgency: 'immediate',
        implementationTime: 'immediate'
      });
    });
  }
  
  return recommendations;
};

// 🔥 ВИПРАВЛЕННЯ: Безпечний хук для сповіщень
const useSafeNotifications = () => {
  try {
    return useNotifications();
  } catch (error) {
    console.warn('NotificationProvider not available:', error.message);
    // Повертаємо заглушку
    return {
      notifyAIRecommendation: () => console.log('NotificationProvider not available'),
      notifyAnomalyDetection: () => console.log('NotificationProvider not available'),
      showNotification: () => console.log('NotificationProvider not available'),
      notifyEfficiencyAlert: () => console.log('NotificationProvider not available'),
      notifyResourceAlert: () => console.log('NotificationProvider not available'),
      notifyMaintenance: () => console.log('NotificationProvider not available')
    };
  }
};

export const useAIRecommendations = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState(null);
  
  // 🔥 ВИПРАВЛЕННЯ: Використовуємо безпечний хук
  const { 
    notifyAIRecommendation, 
    notifyAnomalyDetection,
    showNotification,
    notifyEfficiencyAlert,
    notifyResourceAlert,
    notifyMaintenance
  } = useSafeNotifications();
  
  const { t } = useI18n();

  // Функція аналізу даних
  const analyzeData = useCallback(async (productionData = [], meterData = [], maintenanceData = []) => {
    setIsAnalyzing(true);
    
    try {
      console.log('🔍 Starting AI analysis...');
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const detectedAnomalies = detectAnomalies({
        efficiency: productionData.length > 0 ? productionData[0].efficiency : 0,
        waterConsumption: meterData.find(m => m.type === 'water')?.consumption,
        waterThreshold: 1000,
        propaneConsumption: meterData.find(m => m.type === 'propane')?.consumption,
        propaneThreshold: 500
      });
      
      setAnomalies(detectedAnomalies);
      
      const newRecommendations = analyzeDataForRecommendations(
        productionData, 
        meterData, 
        maintenanceData
      );
      
      setRecommendations(newRecommendations);
      setLastAnalysis(new Date());
      
      // Автоматичні сповіщення (тільки якщо провайдер доступний)
      detectedAnomalies.forEach(anomaly => {
        if (anomaly.severity === 'critical') {
          notifyAnomalyDetection(anomaly);
        }
      });
      
      if (newRecommendations.length > 0) {
        const criticalRecommendations = newRecommendations.filter(rec => rec.priority === 'critical');
        if (criticalRecommendations.length > 0) {
          showNotification(
            t('ai_recommendations_title'),
            t('ai_critical_issues_found', { count: criticalRecommendations.length }),
            'efficiency_alert',
            'critical'
          );
        }
      }
      
      console.log('✅ AI analysis completed:', {
        recommendations: newRecommendations.length,
        anomalies: detectedAnomalies.length
      });
      
      return {
        recommendations: newRecommendations,
        anomalies: detectedAnomalies
      };
      
    } catch (error) {
      console.error('❌ AI analysis error:', error);
      showNotification(
        t('ai_recommendations_title'),
        t('common_loadError'),
        'anomaly_detected',
        'medium'
      );
    } finally {
      setIsAnalyzing(false);
    }
  }, [notifyAIRecommendation, notifyAnomalyDetection, showNotification, t]);

  // Функція для ручного відправлення рекомендації
  const sendRecommendation = useCallback((recommendation) => {
    notifyAIRecommendation(recommendation);
  }, [notifyAIRecommendation]);

  // Функція для відправлення сповіщення про ефективність
  const sendEfficiencyAlert = useCallback((efficiencyData) => {
    notifyEfficiencyAlert(efficiencyData);
  }, [notifyEfficiencyAlert]);

  // Функція для відправлення сповіщення про ресурси
  const sendResourceAlert = useCallback((resourceData) => {
    notifyResourceAlert(resourceData);
  }, [notifyResourceAlert]);

  // Функція для відправлення сповіщення про технічне обслуговування
  const sendMaintenanceAlert = useCallback((maintenanceData) => {
    notifyMaintenance(maintenanceData);
  }, [notifyMaintenance]);

  // Функція для відправлення сповіщення про аномалію
  const sendAnomalyAlert = useCallback((anomalyData) => {
    notifyAnomalyDetection(anomalyData);
  }, [notifyAnomalyDetection]);

  // Функція для отримання рекомендацій за пріоритетом
  const getRecommendationsByPriority = useCallback((priority = null) => {
    if (!priority) return recommendations;
    return recommendations.filter(rec => rec.priority === priority);
  }, [recommendations]);

  // Функція для отримання аномалій за серйозністю
  const getAnomaliesBySeverity = useCallback((severity = null) => {
    if (!severity) return anomalies;
    return anomalies.filter(anomaly => anomaly.severity === severity);
  }, [anomalies]);

  // Функція для маркування рекомендації як застосованої
  const markRecommendationApplied = useCallback((recommendationId) => {
    setRecommendations(prev => 
      prev.filter(rec => rec.id !== recommendationId)
    );
    
    showNotification(
      t('common_success'),
      t('ai_recommendation_applied'),
      'production_target',
      'info'
    );
  }, [showNotification, t]);

  // Автоматичний аналіз при завантаженні
  useEffect(() => {
    // Автоматичний аналіз через 5 секунд після завантаження
    const timer = setTimeout(() => {
      analyzeData();
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [analyzeData]);

  return {
    // Стан
    recommendations,
    anomalies,
    isAnalyzing,
    lastAnalysis,
    
    // Основні функції
    analyzeData,
    sendRecommendation,
    
    // Спеціалізовані функції сповіщень
    sendEfficiencyAlert,
    sendResourceAlert, 
    sendMaintenanceAlert,
    sendAnomalyAlert,
    
    // Допоміжні функції
    getRecommendationsByPriority,
    getAnomaliesBySeverity,
    markRecommendationApplied,
    
    // Статистика
    stats: {
      totalRecommendations: recommendations.length,
      criticalRecommendations: recommendations.filter(r => r.priority === 'critical').length,
      totalAnomalies: anomalies.length,
      criticalAnomalies: anomalies.filter(a => a.severity === 'critical').length
    },
    
    // Утиліти
    hasCriticalIssues: recommendations.some(r => r.priority === 'critical') || 
                      anomalies.some(a => a.severity === 'critical')
  };
};

export default useAIRecommendations;